import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui/select";
import {
    Plus,
    Search,
    Filter,
    Grid2X2,
    List,
    RefreshCw,
    Library,
    FileText,
    Video,
    Layout,
    MessageSquare,
    Package,
    FileArchive,
    Star,
    Share2,
    Upload,
    Save,
    Download
} from 'lucide-react';
import { MaterialCard } from '../components/MaterialCard';
import { toast } from 'sonner';
import { BUSINESS_TYPES, BUSINESS_TYPE_CONTRACTS, SMART_BUNDLES } from '../data/contractsConfig';

const CATEGORY_FILE_TYPES = {
    'sales-materials': ['pdf', 'word', 'excel'],
    'partner-kits': ['pdf', 'powerpoint', 'word'],
    'pitch-decks': ['powerpoint', 'pdf'],
    'outreach-templates': ['word', 'pdf'],
    'contracts': ['word', 'pdf'],
    'campaign-assets': ['mp4', 'mp3', 'image'],
    'all': ['pdf', 'word', 'excel', 'powerpoint', 'mp4', 'mp3', 'image']
};

export const SalesMarketingHub = () => {
    const { t } = useTranslation(['marketing_hub', 'common']);
    const [activeTab, setActiveTab] = useState('sales-materials');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [contractsList, setContractsList] = useState([]);
    const [selectedBusinessType, setSelectedBusinessType] = useState('all');
    const [selectedContractLang, setSelectedContractLang] = useState('bilingual');

    // Initial state with sample data and localStorage migration
    const [materials, setMaterials] = useState(() => {
        const saved = localStorage.getItem('marketing_hub_materials');
        if (saved) {
            return JSON.parse(saved);
        }
        return [
            {
                id: 1,
                title: 'Q1 2026 Product Catalog',
                description: 'Complete list of all new product lines, technical specifications and retail pricing for 2026.',
                file_type: 'pdf',
                category: 'Sales Materials',
                tags: ['Pricing', 'Catalog', '2026'],
                owner: 'Sarah Connor',
                updated_at: 'Mar 02, 2026',
                tab: 'sales-materials',
                file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
            },
            {
                id: 2,
                title: 'Partner Training Kit - V2',
                description: 'Comprehensive onboarding materials for new implementation partners and distributors.',
                file_type: 'powerpoint',
                category: 'Partner Kits',
                tags: ['Onboarding', 'Partner'],
                owner: 'John Doe',
                updated_at: 'Feb 28, 2026',
                tab: 'partner-kits',
                file_url: ''
            },
            {
                id: 3,
                title: 'Corporate Presentation 2026',
                description: 'Official company pitch deck for potential investors and high-profile clients.',
                file_type: 'powerpoint',
                category: 'Pitch Decks',
                tags: ['Pitch', 'Corporate'],
                owner: 'Sarah Connor',
                updated_at: 'Mar 01, 2026',
                tab: 'pitch-decks',
                file_url: ''
            },
            {
                id: 4,
                title: 'Email Outreach Templates',
                description: 'Standardized email templates for initial cold outreach and follow-up sequences.',
                file_type: 'word',
                category: 'Outreach Templates',
                tags: ['Templates', 'Email'],
                owner: 'Admin',
                updated_at: 'Feb 15, 2026',
                tab: 'outreach-templates',
                file_url: ''
            },
            {
                id: 5,
                title: 'Product Launch Video',
                description: 'Promotional video showcasing the key features of the new EzERP mobile module.',
                file_type: 'mp4',
                category: 'Campaign Assets',
                tags: ['Launch', 'Video'],
                owner: 'Media Team',
                updated_at: 'Mar 01, 2026',
                tab: 'campaign-assets',
                file_url: 'https://sample-videos.com/video123/mp4/720/big_buck_bunny_720p_1mb.mp4'
            },
            {
                id: 6,
                title: 'Master Service Agreement Template',
                description: 'Standard MSA for all new service-based contracts and partnership renewals.',
                file_type: 'word',
                category: 'Contracts',
                tags: ['Legal', 'MSA'],
                owner: 'Legal Dept',
                updated_at: 'Jan 10, 2026',
                tab: 'contracts',
                file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
            }
        ];
    });

    // Persist to localStorage whenever materials change
    useEffect(() => {
        localStorage.setItem('marketing_hub_materials', JSON.stringify(materials));
    }, [materials]);

    // Fetch dynamic B2B contract templates list from backend
    const loadContracts = () => {
        fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/crm/marketing-hub/contracts`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('ezerpToken') || ''}`
            }
        })
        .then(res => {
            if (!res.ok) throw new Error('Failed to fetch');
            return res.json();
        })
        .then(data => {
            setContractsList(data);
        })
        .catch(err => {
            console.error("Error loading B2B contracts", err);
            setContractsList([]);
        });
    };

    useEffect(() => {
        loadContracts();
    }, []);

    // Reload contracts when user switches to contracts tab
    useEffect(() => {
        if (activeTab === 'contracts') {
            loadContracts();
        }
    }, [activeTab]);

    const handlePreview = (material) => {
        // Use internal secure previewer
        window.open(`/crm/marketing-hub/preview/${material.id}`, '_blank');
    };

    const handleDownloadWord = (contractCode) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        toast.promise(
            fetch(`${backendUrl}/api/crm/marketing-hub/contracts/${contractCode}/download?lang=${selectedContractLang}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('ezerpToken') || ''}`
                }
            })
            .then(res => {
                if (!res.ok) throw new Error('Download failed');
                return res.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${contractCode}_${selectedContractLang}.docx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }),
            {
                loading: 'Fetching Word document...',
                success: 'Contract Word document downloaded successfully!',
                error: 'Failed to download contract document.'
            }
        );
    };

    const handleDownloadZip = () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        toast.promise(
            fetch(`${backendUrl}/api/crm/marketing-hub/contracts/bundle/download?business_type=${selectedBusinessType}&lang=${selectedContractLang}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('ezerpToken') || ''}`
                }
            })
            .then(res => {
                if (!res.ok) throw new Error('ZIP download failed');
                return res.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `EzERP_Bundle_${selectedBusinessType}_${selectedContractLang}.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }),
            {
                loading: 'Generating and packaging onboarding ZIP...',
                success: 'Onboarding ZIP downloaded successfully!',
                error: 'Failed to download onboarding ZIP.'
            }
        );
    };


    // Form state for new material
    const [newMaterial, setNewMaterial] = useState({
        title: '',
        description: '',
        file_type: '',
        category: 'sales-materials',
        tags: ''
    });

    // Update category in form when tab changes
    useEffect(() => {
        if (activeTab !== 'all') {
            setNewMaterial(prev => ({ ...prev, category: activeTab }));
        }
    }, [activeTab]);

    const handleRefresh = () => {
        setLoading(true);
        loadContracts();
        setTimeout(() => {
            setLoading(false);
            toast.success(t('sync_success'));
        }, 1000);
    };

    const handleAddMaterial = (e) => {
        e.preventDefault();

        if (!newMaterial.title || !newMaterial.file_type) {
            toast.error(t('fill_required'));
            return;
        }

        const materialToAdd = {
            id: materials.length > 0 ? Math.max(...materials.map(m => m.id)) + 1 : 1,
            ...newMaterial,
            category: TABS_CONFIG.find(t_cfg => t_cfg.id === newMaterial.category)?.label_key ? t(TABS_CONFIG.find(t_cfg => t_cfg.id === newMaterial.category).label_key) : 'Other',
            tab: newMaterial.category,
            tags: newMaterial.tags.split(',').map(t_tag => t_tag.trim()).filter(t_tag => t_tag !== ''),
            owner: 'Admin User',
            updated_at: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
            file_url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
        };

        setMaterials([materialToAdd, ...materials]);
        setIsAddDialogOpen(false);
        setNewMaterial({
            title: '',
            description: '',
            file_type: '',
            category: activeTab === 'all' ? 'sales-materials' : activeTab,
            tags: ''
        });

        toast.success(t('add_success', { title: materialToAdd.title }));
    };

    const handleEditClick = (material) => {
        setEditingMaterial({
            ...material,
            category: material.tab, // Use the stored tab ID
            tags: Array.isArray(material.tags) ? material.tags.join(', ') : ''
        });
        setIsEditDialogOpen(true);
    };

    const handleUpdateMaterial = (e) => {
        e.preventDefault();
        if (!editingMaterial.title || !editingMaterial.file_type) {
            toast.error(t('fill_required'));
            return;
        }

        const updatedMaterials = materials.map(m => {
            if (m.id === editingMaterial.id) {
                return {
                    ...m,
                    ...editingMaterial,
                    category: TABS_CONFIG.find(t_cfg => t_cfg.id === editingMaterial.category)?.label_key ? t(TABS_CONFIG.find(t_cfg => t_cfg.id === editingMaterial.category).label_key) : m.category,
                    tab: editingMaterial.category,
                    tags: editingMaterial.tags.split(',').map(t_tag => t_tag.trim()).filter(t_tag => t_tag !== ''),
                    updated_at: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
                    file_url: editingMaterial.file_url || m.file_url || 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
                };
            }
            return m;
        });

        setMaterials(updatedMaterials);
        setIsEditDialogOpen(false);
        setEditingMaterial(null);
        toast.success(t('update_success'));
    };

    const handleDeleteMaterial = (material) => {
        if (window.confirm(t('delete_confirm', { title: material.title }))) {
            setMaterials(materials.filter(m => m.id !== material.id));
            toast.success(t('delete_success'));
        }
    };

    const filteredMaterials = materials.filter(m =>
        (activeTab === 'all' || m.tab === activeTab) &&
        (m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const filteredContracts = contractsList.filter(c => {
        const matchesBusiness = selectedBusinessType === 'all' || 
            (BUSINESS_TYPE_CONTRACTS[selectedBusinessType] || []).includes(c.code);
        const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.folder.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesBusiness && matchesSearch;
    });

    const TABS_CONFIG = [
        { id: 'sales-materials', label_key: 'marketing_hub:tabs.sales-materials', icon: FileText },
        { id: 'partner-kits', label_key: 'marketing_hub:tabs.partner-kits', icon: Package },
        { id: 'pitch-decks', label_key: 'marketing_hub:tabs.pitch-decks', icon: Layout },
        { id: 'outreach-templates', label_key: 'marketing_hub:tabs.outreach-templates', icon: MessageSquare },
        { id: 'contracts', label_key: 'marketing_hub:tabs.contracts', icon: FileArchive },
        { id: 'campaign-assets', label_key: 'marketing_hub:tabs.campaign-assets', icon: Star },
        { id: 'all', label_key: 'marketing_hub:tabs.all', icon: Grid2X2 }
    ];

    return (
        <div className="space-y-6" data-testid="sales-marketing-hub">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] text-foreground flex items-center gap-3">
                        <Library className="h-8 w-8 text-teal-600" />
                        {t('hub_title')}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-lg">
                        {t('hub_subtitle')}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={handleRefresh}>
                        <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
                    </Button>

                    <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-teal-600 hover:bg-teal-700 gap-2">
                                <Plus className="h-4 w-4" />
                                {t('add_material')}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[500px] border-none shadow-2xl overflow-hidden rounded-2xl">
                            <DialogHeader className="bg-teal-600/5 -mx-6 -mt-6 p-6 border-b border-teal-500/10">
                                <DialogTitle className="text-2xl font-bold font-['Space_Grotesk'] flex items-center gap-2">
                                    <Plus className="h-6 w-6 text-teal-600" />
                                    {t('add_material')}
                                </DialogTitle>
                                <DialogDescription className="text-base text-muted-foreground mt-1">
                                    {t('hub_subtitle')}
                                </DialogDescription>
                            </DialogHeader>

                            <form onSubmit={handleAddMaterial} className="space-y-5 pt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="title" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.title')} *</Label>
                                    <Input
                                        id="title"
                                        placeholder="e.g. Sales Playbook 2026"
                                        className="h-11 border-border/50 focus:border-teal-500 transition-colors"
                                        value={newMaterial.title}
                                        onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="category" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.category')}</Label>
                                        <Select
                                            value={newMaterial.category}
                                            onValueChange={(val) => setNewMaterial({ ...newMaterial, category: val, file_type: '' })}
                                        >
                                            <SelectTrigger className="h-11 border-border/50">
                                                <SelectValue placeholder={t('common:select')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TABS_CONFIG.filter(t_cfg => t_cfg.id !== 'all').map(tab => (
                                                    <SelectItem key={tab.id} value={tab.id}>{t(tab.label_key)}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="file_type" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.file_type')} *</Label>
                                        <Select
                                            value={newMaterial.file_type}
                                            onValueChange={(val) => setNewMaterial({ ...newMaterial, file_type: val })}
                                        >
                                            <SelectTrigger className="h-11 border-border/50">
                                                <SelectValue placeholder={t('common:type')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(CATEGORY_FILE_TYPES[newMaterial.category] || []).map(type => (
                                                    <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="description" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.description')}</Label>
                                    <Textarea
                                        id="description"
                                        placeholder="..."
                                        className="min-h-[100px] border-border/50 focus:border-teal-500 transition-colors resize-none"
                                        value={newMaterial.description}
                                        onChange={(e) => setNewMaterial({ ...newMaterial, description: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="tags" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.tags')}</Label>
                                    <Input
                                        id="tags"
                                        placeholder="e.g. Sales, 2026, Q1"
                                        className="h-11 border-border/50 focus:border-teal-500 transition-colors"
                                        value={newMaterial.tags}
                                        onChange={(e) => setNewMaterial({ ...newMaterial, tags: e.target.value })}
                                    />
                                </div>

                                <DialogFooter className="pt-4 mt-2 border-t border-border/50 -mx-6 px-6">
                                    <Button type="button" variant="ghost" onClick={() => setIsAddDialogOpen(false)}>
                                        {t('form.cancel')}
                                    </Button>
                                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700 gap-2 min-w-[120px]">
                                        <Upload className="h-4 w-4" />
                                        {t('form.save')}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="sm:max-w-[500px] border-none shadow-2xl overflow-hidden rounded-2xl">
                    <DialogHeader className="bg-amber-500/5 -mx-6 -mt-6 p-6 border-b border-amber-500/10">
                        <DialogTitle className="text-2xl font-bold font-['Space_Grotesk'] flex items-center gap-2">
                            <Library className="h-6 w-6 text-amber-600" />
                            {t('edit_material')}
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground mt-1">
                            {t('edit_description')}
                        </DialogDescription>
                    </DialogHeader>

                    {editingMaterial && (
                        <form onSubmit={handleUpdateMaterial} className="space-y-5 pt-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-title" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.title')} *</Label>
                                <Input
                                    id="edit-title"
                                    className="h-11 border-border/50 focus:border-amber-500 transition-colors"
                                    value={editingMaterial.title}
                                    onChange={(e) => setEditingMaterial({ ...editingMaterial, title: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-category" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.category')}</Label>
                                    <Select
                                        value={editingMaterial.category}
                                        onValueChange={(val) => setEditingMaterial({ ...editingMaterial, category: val })}
                                    >
                                        <SelectTrigger className="h-11 border-border/50">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TABS_CONFIG.filter(t_cfg => t_cfg.id !== 'all').map(tab => (
                                                <SelectItem key={tab.id} value={tab.id}>{t(tab.label_key)}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="edit-file_type" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.file_type')} *</Label>
                                    <Select
                                        value={editingMaterial.file_type}
                                        onValueChange={(val) => setEditingMaterial({ ...editingMaterial, file_type: val })}
                                    >
                                        <SelectTrigger className="h-11 border-border/50">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(CATEGORY_FILE_TYPES[editingMaterial.category] || []).map(type => (
                                                <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-description" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.description')}</Label>
                                <Textarea
                                    id="edit-description"
                                    className="min-h-[100px] border-border/50 focus:border-amber-500 transition-colors resize-none"
                                    value={editingMaterial.description}
                                    onChange={(e) => setEditingMaterial({ ...editingMaterial, description: e.target.value })}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-tags" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('form.tags')}</Label>
                                <Input
                                    id="edit-tags"
                                    className="h-11 border-border/50 focus:border-amber-500 transition-colors"
                                    value={editingMaterial.tags}
                                    onChange={(e) => setEditingMaterial({ ...editingMaterial, tags: e.target.value })}
                                />
                            </div>

                            <DialogFooter className="pt-4 mt-2 border-t border-border/50 -mx-6 px-6">
                                <Button type="button" variant="ghost" onClick={() => setIsEditDialogOpen(false)}>
                                    {t('form.cancel')}
                                </Button>
                                <Button type="submit" className="bg-amber-600 hover:bg-amber-700 gap-2 min-w-[120px] text-white">
                                    <Save className="h-4 w-4" />
                                    {t('form.update')}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* KPI Stats (Minimal) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label_key: 'total_files', value: materials.length.toString(), icon: Grid2X2, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label_key: 'shared_lately', value: '12', icon: Share2, color: 'text-purple-600', bg: 'bg-purple-50' },
                    { label_key: 'active_campaigns', value: '5', icon: Star, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label_key: 'favorites', value: '28', icon: Star, color: 'text-pink-600', bg: 'bg-pink-50' }
                ].map((stat, i) => (
                    <Card key={i} className="card-elevated border-none">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={cn("p-2 rounded-lg", stat.bg)}>
                                <stat.icon className={cn("h-5 w-5", stat.color)} />
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{t(stat.label_key)}</p>
                                <p className="text-xl font-bold font-['Space_Grotesk']">{stat.value}</p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Control Bar */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-2 rounded-xl border border-border/50 shadow-sm">
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t('search_placeholder')}
                        className="pl-9 bg-muted/20 border-border/50 focus:border-teal-500 transition-colors"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button variant="outline" size="sm" className="gap-2 text-xs">
                        <Filter className="h-3 w-3" />
                        {t('filters')}
                    </Button>
                    <div className="h-8 w-[1px] bg-border mx-1" />
                    <div className="flex bg-muted/40 p-1 rounded-lg">
                        <Button
                            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setViewMode('grid')}
                        >
                            <Grid2X2 className="h-4 w-4" />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setViewMode('list')}
                        >
                            <List className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Tabs & Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="flex overflow-x-auto h-auto p-1 bg-muted/30 gap-1 scrollbar-hide justify-start mb-6 rounded-xl">
                    {TABS_CONFIG.map(tab => (
                        <TabsTrigger
                            key={tab.id}
                            value={tab.id}
                            className="px-4 py-2.5 rounded-lg data-[state=active]:bg-teal-600 data-[state=active]:text-white transition-all flex items-center gap-2"
                        >
                            <tab.icon className="h-4 w-4" />
                            {t(tab.label_key)}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <div className="mt-6">
                    {activeTab === 'contracts' ? (
                        <div className="space-y-6">
                            {/* Business type and language selects row */}
                            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border/50 shadow-sm">
                                <div className="flex flex-col sm:flex-row gap-4 w-full">
                                    <div className="w-full sm:w-72 space-y-1.5">
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Select Business Type</Label>
                                        <Select value={selectedBusinessType} onValueChange={setSelectedBusinessType}>
                                            <SelectTrigger className="h-10 border-border/50 bg-background">
                                                <SelectValue placeholder="Filter by Business Type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {BUSINESS_TYPES.map(type => (
                                                    <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="w-full sm:w-72 space-y-1.5">
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contract Jurisdiction / Language</Label>
                                        <Select value={selectedContractLang} onValueChange={setSelectedContractLang}>
                                            <SelectTrigger className="h-10 border-border/50 bg-background">
                                                <SelectValue placeholder="Select Language" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="bilingual">Bilingual (EN / PT)</SelectItem>
                                                <SelectItem value="br-pt">Portuguese (BR-PT)</SelectItem>
                                                <SelectItem value="en-ontario">English (Ontario, Canada)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Smart Bundle Panel */}
                            {selectedBusinessType !== 'all' && SMART_BUNDLES[selectedBusinessType] && (
                                <div className="bg-gradient-to-br from-teal-500/5 via-transparent to-transparent border border-teal-500/10 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 rounded-full blur-3xl pointer-events-none group-hover:scale-125 transition-transform" />
                                    <div className="space-y-2 max-w-2xl text-left">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bold text-teal-600 bg-teal-50 dark:bg-teal-950/40 px-2.5 py-0.5 rounded-full uppercase tracking-wider font-mono">Recommended Onboarding Set</span>
                                            <span className="text-[10px] font-mono text-muted-foreground uppercase">{SMART_BUNDLES[selectedBusinessType].core.length + SMART_BUNDLES[selectedBusinessType].rider.length + SMART_BUNDLES[selectedBusinessType].operational.length} Documents</span>
                                        </div>
                                        <h3 className="text-xl font-bold font-['Space_Grotesk'] text-foreground">{SMART_BUNDLES[selectedBusinessType].title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{SMART_BUNDLES[selectedBusinessType].description}</p>
                                    </div>
                                    <Button 
                                        className="bg-teal-600 hover:bg-teal-700 text-white font-bold gap-2 min-w-[200px] h-11 rounded-xl shadow-md shrink-0 transition-all hover:translate-y-[-1px] active:translate-y-0"
                                        onClick={handleDownloadZip}
                                    >
                                        <Download className="h-4 w-4" />
                                        Download Onboarding ZIP
                                    </Button>
                                </div>
                            )}

                            {/* Dynamic tabular listing */}
                            {filteredContracts.length > 0 ? (
                                <div className="overflow-x-auto rounded-xl border border-border/50 bg-card shadow-sm">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-border/50 bg-muted/20 text-muted-foreground text-xs font-semibold uppercase tracking-wider">
                                                <th className="p-4">Contract Name / Code</th>
                                                <th className="p-4">Category Folder</th>
                                                <th className="p-4">Jurisdiction Context</th>
                                                <th className="p-4 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/40 text-sm">
                                            {filteredContracts.map(contract => (
                                                <tr key={contract.code} className="hover:bg-muted/10 transition-colors">
                                                    <td className="p-4 font-medium flex items-center gap-2">
                                                        <span className="font-mono text-teal-600 bg-teal-50 dark:bg-teal-950/30 px-2 py-0.5 rounded text-xs">{contract.code}</span>
                                                        <span className="text-foreground font-semibold">{contract.name}</span>
                                                    </td>
                                                    <td className="p-4 text-muted-foreground text-xs">{contract.folder}</td>
                                                    <td className="p-4">
                                                        <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                                                            {selectedContractLang === 'en-ontario' ? 'Ontario (EN)' : 
                                                             selectedContractLang === 'br-pt' ? 'Brazil (PT)' : 'Bilingual (EN/PT)'}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 text-right space-x-2">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            className="h-8 text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950/20 gap-1.5"
                                                            onClick={() => window.open(`/crm/marketing-hub/preview/${contract.code}?lang=${selectedContractLang}`, '_blank')}
                                                        >
                                                            <FileText className="h-3.5 w-3.5" />
                                                            Preview
                                                        </Button>
                                                        <Button 
                                                            variant="outline" 
                                                            size="sm" 
                                                            className="h-8 border-teal-500/20 hover:border-teal-500 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-950/20 gap-1.5"
                                                            onClick={() => handleDownloadWord(contract.code)}
                                                        >
                                                            <Download className="h-3.5 w-3.5" />
                                                            Download Word
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <Card className="card-elevated border-dashed border-2 py-20 text-center">
                                    <CardContent>
                                        <Library className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                                        <h3 className="text-xl font-bold font-['Space_Grotesk']">No contracts matched the filters</h3>
                                        <p className="text-muted-foreground max-w-xs mx-auto mt-2">
                                            Try clearing your search query or selecting a different business type.
                                        </p>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    ) : filteredMaterials.length > 0 ? (
                        <div className={cn(
                            "grid gap-6",
                            viewMode === 'grid' ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"
                        )}>
                            {filteredMaterials.map(material => (
                                <MaterialCard
                                    key={material.id}
                                    material={material}
                                    onEdit={handleEditClick}
                                    onDelete={handleDeleteMaterial}
                                    onPreview={handlePreview}
                                />
                            ))}
                        </div>
                    ) : (
                        <Card className="card-elevated border-dashed border-2 py-20 text-center">
                            <CardContent>
                                <Library className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="text-xl font-bold font-['Space_Grotesk']">{t('no_materials')}</h3>
                                <p className="text-muted-foreground max-w-xs mx-auto mt-2">
                                    {t('no_materials_desc')}
                                </p>
                                <Button variant="outline" className="mt-6 font-semibold" onClick={() => { setSearchQuery(''); setActiveTab('all'); }}>
                                    {t('clear_filters')}
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </Tabs>
        </div>
    );
};
